import React, { useState, useMemo } from "react";
import { Col, Row } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { createIdentifierReportSchema } from "../../../utils/validationUtils";
import { generateIdentifierReport } from "../../../api/reportsService";
import { transformFormToApiParams, } from "../../../types/reports/identifierReport";
import { searchPublisherPrefixesByIdentifier, fetchPublisherPrefixById, } from "../../../api/publisherPrefixService";
import { RecekSelection } from "../../inputs";
import { useReportMutation } from "../../../hooks/useReportMutation";
import { ReportSubmitSection } from "./ReportSubmitSection";
import { formatPublisherPrefixLabel } from "../../../utils/publisherPrefixUtils";
import { usePermissions } from "../../../hooks/usePermissions";
import { useGetCurrentUser } from "../../../utils/useCurrentUser";
export var IdentifierReportForm = function () {
    var _a;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var isPublisherRoleOnly = usePermissions().isPublisherRoleOnly;
    var currentUser = useGetCurrentUser().data;
    var ownPublisherSourceId = isPublisherRoleOnly
        ? (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _a === void 0 ? void 0 : _a.sourceId
        : undefined;
    var _b = useState(""), searchQuery = _b[0], setSearchQuery = _b[1];
    var _c = useState(""), selectedPrefixId = _c[0], setSelectedPrefixId = _c[1];
    var _d = useForm({
        resolver: zodResolver(createIdentifierReportSchema(formatMessage)),
        defaultValues: {
            publisherPrefixId: "",
        },
    }), control = _d.control, handleSubmit = _d.handleSubmit, setValue = _d.setValue;
    var _e = useQuery(["publisher-prefix-selected", selectedPrefixId], function () { return fetchPublisherPrefixById(ky, selectedPrefixId); }, {
        enabled: !!selectedPrefixId,
        staleTime: 5 * 60 * 1000,
    }), selectedPrefixData = _e.data, isLoadingSelectedPrefix = _e.isLoading;
    var _f = useQuery(["publisher-prefixes-search", searchQuery, ownPublisherSourceId], function () {
        return searchPublisherPrefixesByIdentifier(ky, {
            search: searchQuery.trim(),
            publisherSourceId: ownPublisherSourceId,
            limit: 100,
        });
    }, {
        enabled: !!searchQuery.trim(),
        staleTime: 30000,
    }), searchResultsData = _f.data, isLoadingSearch = _f.isLoading;
    var publisherPrefixOptions = useMemo(function () {
        var options = [];
        if (selectedPrefixData && selectedPrefixId) {
            options.push({
                value: selectedPrefixData.id || "",
                label: formatPublisherPrefixLabel(selectedPrefixData),
            });
        }
        var searchResults = (searchResultsData === null || searchResultsData === void 0 ? void 0 : searchResultsData.items) || [];
        searchResults.forEach(function (prefix) {
            if (prefix.id !== selectedPrefixId) {
                options.push({
                    value: prefix.id || "",
                    label: formatPublisherPrefixLabel(prefix),
                });
            }
        });
        return options;
    }, [selectedPrefixData, selectedPrefixId, searchResultsData]);
    var reportMutation = useReportMutation({
        reportType: "identifier-report",
        generateReport: function (ky, data) {
            var apiParams = transformFormToApiParams(data);
            return generateIdentifierReport(ky, apiParams);
        },
        successMessageId: "ui-br-agency.outputs.identifier-report.success.generate",
        errorMessageId: "ui-br-agency.outputs.identifier-report.error.generate",
    });
    var handlePrefixChange = function (value) {
        setSelectedPrefixId(value);
        setValue("publisherPrefixId", value);
    };
    var handleSearchInput = function (inputValue) {
        setSearchQuery(inputValue);
    };
    var isLoadingData = isLoadingSearch || isLoadingSelectedPrefix;
    return (React.createElement("form", { onSubmit: handleSubmit(function (data) { return reportMutation.mutate(data); }) },
        React.createElement("h2", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.identifier-report" })),
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.identifier-report.description" })),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 12, md: 6 },
                React.createElement(Controller, { control: control, name: "publisherPrefixId", render: function (_a) {
                        var _b;
                        var field = _a.field, fieldState = _a.fieldState;
                        return (React.createElement(RecekSelection, { labelId: "ui-br-agency.outputs.identifier-report.field.publisherPrefixId", asyncFilter: true, value: field.value, loading: isLoadingData, onFilter: handleSearchInput, onChange: handlePrefixChange, dataOptions: publisherPrefixOptions, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message }));
                    } }))),
        React.createElement(ReportSubmitSection, { reportJob: reportMutation.reportJob, isLoading: reportMutation.isLoading, generateButtonId: "ui-br-agency.outputs.identifier-report.button.generate", successMessage: reportMutation.successMessage, onSuccessDismiss: reportMutation.clearSuccessMessage, errorModalOpen: reportMutation.errorModalOpen, errorMessage: reportMutation.errorMessage, onErrorClose: reportMutation.closeErrorModal, disabled: isLoadingData })));
};
